import { IncomeSourceStatus } from "shared/types";
import { toMoney } from "shared/utils";
import { UploadedIncomeSource } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { IncomeField } from "./types";
import { getItemDocument, isFraudulentItem } from "./utils";

type Form1099Form = {
  form_type?: string | null;
  employer_name?: string | null;
  fields?: { payer_tin?: { value?: unknown } };
  year?: string | number | null;
  annual_gross_income_amount?: number | null;
  annual_net_income_amount?: number | null;
};

const COUNTED_STATUSES: string[] = [
  IncomeSourceStatus.Finished,
  IncomeSourceStatus.FinishedWithWarnings
];

const itemForms = (item: VerifiedItem): Form1099Form[] =>
  (getItemDocument(item)?.income_sources ?? []).flatMap(
    (source) => (source as { forms?: Form1099Form[] }).forms ?? []
  );

const documentIdentity = (forms: Form1099Form[]) =>
  forms
    .map((form) =>
      [
        form.form_type,
        form.fields?.payer_tin?.value ?? form.employer_name,
        form.year,
        form.annual_gross_income_amount
      ].join("|")
    )
    .sort()
    .join(",");

// Same rules as the API: only verified, non-fraudulent sources, re-uploads counted once,
// summed per tax year and averaged across years.
export const form1099Formula = (
  items: VerifiedItem[],
  field: IncomeField,
  sources: Pick<UploadedIncomeSource, "id" | "status">[] = []
): string | null => {
  const statusBySourceId = new Map(
    sources.map((source) => [source.id, source.status])
  );
  const seenDocuments = new Set<string>();

  const totalsByYear = items
    .filter(
      (item) =>
        COUNTED_STATUSES.includes(
          statusBySourceId.get(item.income_source_id) ?? ""
        ) && !isFraudulentItem(item)
    )
    .map(itemForms)
    .filter((forms) => {
      const identity = documentIdentity(forms);
      if (!forms.length || seenDocuments.has(identity)) return false;
      seenDocuments.add(identity);
      return true;
    })
    .flat()
    .reduce<Map<string, number[]>>((acc, form) => {
      const amount =
        (field === "gross"
          ? form.annual_gross_income_amount
          : form.annual_net_income_amount) ?? 0;
      const year = String(form.year ?? "");
      acc.set(year, [...(acc.get(year) ?? []), amount]);
      return acc;
    }, new Map());

  const yearTerms = [...totalsByYear.values()].map((amounts) =>
    amounts.map((amount) => toMoney(amount)).join(" + ")
  );
  if (!yearTerms.length) return null;
  if (yearTerms.length === 1) return `(${yearTerms[0]})/12`;

  const wrappedTerms = yearTerms.map((term) => "(" + term + ")").join(" + ");
  return `((${wrappedTerms})/${yearTerms.length})/12`;
};
